import { useEffect, useState } from "react";

import { IconButton, Chip, Tooltip } from "@mui/material";
import { Download, Upload, Reply } from "@mui/icons-material";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { format, parseISO } from "date-fns";

import { useLoading } from "@shared/hooks/useLoading";
import { useUserProfile } from "@shared/hooks/useUserProfile";
import { MainLayout } from "@shared/ui/MainLayout";
import MDBox from "@vendor/components/MDBox";
import { InvoiceItem } from "../components/InvoiceItem";
import { CustomToolbarReloadComponent } from "@shared/ui/MuiTable/CustomToolbarReloadComponent";
import { DataGridTable } from "@shared/ui/DataGridTable";
import { ErrorAlert } from "@shared/ui/alerts";
import { getFormatDate } from "@shared/utils/getFormatDate";
import { subType, types, statusForInvoice } from "../components/CustomChip";
import { ChargedInvoiceAutomaticDialog } from "../components/ChargedInvoiceAutomaticDialog";
import { ResendInvoiceManual } from "../components/ResendInvoiceManual";
import { transformToDTO } from "../lib/invoiceRow";
import {
  getAllSubmissions,
  downloadInvoice,
  cancelInvoice as cancelInvoiceRequest,
} from "../api/invoiceApi";

import type { GridColDef } from "@mui/x-data-grid";
import type { InvoiceRow } from "../lib/invoiceRow";

/** Campos de `userData` (hook legado `useUserProfile`, tipado `unknown`) que esta page lee o pasa a sus hijos. */
interface InvoicePageUserData {
  firstName?: string;
  lastName?: string;
  email?: string;
}

export const InvoicePage = () => {
  const {t, i18n: {language}} = useTranslation();
  const { loading, thisLoading, notLoading } = useLoading();
  const [invoiceList, setInvoiceList] = useState<InvoiceRow[]>([]);
  const [showDialog, setShowDialog] = useState(false)
  const [showDialogResend, setShowDialogResend] = useState(false)
  const [invoiceToSend, setInvoiceToSend] = useState<number | null>(null)
  const [invoiceToResend, setInvoiceToResend] = useState<number | null>(null)
  const { userData } = useUserProfile();
  // `useUserProfile` tipa `userData` como `unknown` (llega de un endpoint sin
  // contrato tipado todavía); acá se castea una sola vez a los campos que esta
  // page y sus hijos (`InvoiceItem`, `ChargedInvoiceAutomaticDialog`) leen.
  const typedUserData = userData as InvoicePageUserData;

  const onOpenDialog = (invoiceId: number) => {
    setInvoiceToSend(invoiceId)
    setShowDialog(true)
  }
  const onCloseDialog = () => setShowDialog(false)

  const onOpenDialogResend = (invoiceId: number) => {
    setInvoiceToResend(invoiceId)
    setShowDialogResend(true)
  }

  const onCloseDialogResend = () => setShowDialogResend(false)

  const getListInvoice = () => {
    getAllSubmissions()
      .then((data) => {
        const transformedData = transformToDTO(data);
        setInvoiceList(transformedData);
      })
      .catch((_error) => {
        ErrorAlert("Error", "Al obtener el listado de facturas").then();
      });
  };

  // Bug pre-logueado 4.16: `invoiceList.find(...)` puede devolver `undefined` si
  // `invoiceId` no está en la lista, y `.status` se lee sin guard. Esta función NO
  // está conectada a ninguna columna ni botón de la UI (la columna "Cancelar" no
  // existe en `columns` — código muerto), por lo que no hay forma práctica de
  // alcanzarla con un test end-to-end/de render para escribir el RED exigido por
  // TDD estricto. Se preserva tal cual (comportamiento preexistente, sin guard
  // nuevo) y se documenta acá en vez de forzar una corrección sin test.
  const cancelInvoice = (invoiceId: number) => {
    const invoiceStatus = invoiceList.find(item => item.id === invoiceId)!;
    if (invoiceStatus.status !== 'CANCELLED') {
      Swal.fire({
        title: "Cancelar factura",
        text: "¿Deseas cancelar esta factura?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Cancelar",
        cancelButtonText: "Salir",
      }).then((result) => {
        if (result.isConfirmed) {
          cancelInvoiceRequest(invoiceId)
            .then(() => {
              Swal.fire({
                title: 'Cancelación exitosa',
                text: 'La factura se ha cancelado exitosamente',
                icon: 'success',
                confirmButtonText: 'OK'
              });
            })
            .catch((error) => {
              console.log("error => ", error);
              Swal.fire({
                title: 'Cancelar factura',
                text: 'Error al cancelar factura',
                icon: 'error',
                confirmButtonText: 'OK'
              });
            })
            .finally(() => {
              getListInvoice();
            })
        }
      });
    } else {
      Swal.fire({
        title: 'Cancelar factura',
        text: 'Esta factura ya se encuentra cancelada',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  };

  const onDownload = (id: number) => {
    thisLoading();
    downloadInvoice(
      id,
      `${t("invoice")}_${typedUserData.firstName}_${typedUserData.lastName}${getFormatDate()}.pdf`
    )
      .finally(() => notLoading());
  };

  // Código muerto: no se invoca desde ningún lado en `InvoicePage` (comportamiento
  // preexistente, se preserva tal cual, incluido el `undefined` que `.at()` puede
  // devolver para un índice fuera de rango).
  const getInvoice = (index: number): InvoiceRow | null | undefined => {
    return invoiceList && invoiceList.length > 0 ? invoiceList.at(index) : null;
  }

  useEffect(() => {
    getListInvoice();
  }, []);

  const columns: GridColDef[] = [
    {
      field: "sentDate",
      headerName: t("sentInvoiceDate"),
      flex: 1,
      valueFormatter: (value) => (value ? format(parseISO(value), 'dd/MM/yyyy') : ""),
    },
    {
      field: "invoiceDate",
      headerName: t("invoiceDate"),
      flex: 1,
      valueFormatter: (value) => (value ? format(parseISO(value), 'dd/MM/yyyy') : ""),
    },
    {
      field: "client",
      headerName: "Cliente",
      flex: 1,
    },
    {
      field: "description",
      headerName: "Descripción",
      flex: 1,
    },
    {
      field: "unitPrice",
      headerName: "Precio Un.",
      flex: 1,
    },
    {
      field: "month",
      headerName: t("month"),
      flex: 1,
    },
    {
      field: "year",
      headerName: t("year"),
      flex: 1,
    },
    {
      field: "discount",
      headerName: t("discount"),
      flex: 1,
    },
    {
      field: "total",
      headerName: t("total"),
      flex: 1,
    },
    {
      field: "currency",
      headerName: t("currency"),
      flex: 1,
    },
    {
      field: "status",
      headerName: t("status"),
      flex: 1,
      renderCell: (params) => statusForInvoice(params.value, t),
    },
    {
      field: "types",
      headerName: t("type"),
      flex: 1,
      renderCell: (params) => types(params.value, t),
    },
    {
      field: "subTypes",
      headerName: t("subtype"),
      flex: 1,
      renderCell: (params) => subType(params.value, t),
    },
    {
      field: "resend",
      headerName: t("resend"),
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const row = params.row;
        if (!row || row.status !== 'REJECTED' || row.types !== "Personal") return "";

        return (
          <Chip
            icon={<Reply style={{ color: 'white' }} />}
            label={t("resend")}
            sx={{ backgroundColor: "#4bd86d", color: "#fff" }}
            onClick={() => {
              onOpenDialogResend(row.id);
            }}
          />
        );
      },
    },
    {
      field: "send",
      headerName: t("send"),
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const row = params.row;
        if (!row || row.status !== 'CREATED') return "";

        return (
          <Chip
            icon={<Upload style={{ color: 'white' }} />}
            label={t("send")}
            sx={{ backgroundColor: "#4bd86d", color: "#fff" }}
            onClick={() => {
              onOpenDialog(row.id);
            }}
          />
        );
      },
    },
    {
      field: "download",
      headerName: t("download"),
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const row = params.row;
        if (!row) return "";

        return (
          <Tooltip title={t("download")}>
            <IconButton
              onClick={() => {
                onDownload(row.invoiceId);
              }}
            >
              <Download />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  const toolbarActions = (
    <CustomToolbarReloadComponent
      title={t("reloadData")}
      onClickAction={getListInvoice}
    />
  );

  return (
    <MainLayout loading={loading}>
      <MDBox py={3}>
        <InvoiceItem
          notLoading={notLoading}
          thisLoading={thisLoading}
          getListInvoice={getListInvoice}
          userData={typedUserData}
        />
        <DataGridTable
          rows={invoiceList}
          title={t("invoices")}
          columns={columns}
          language={language}
          pageSize={20}
          download={false}
          toolbarActions={toolbarActions}
        />
      </MDBox>

      <ChargedInvoiceAutomaticDialog
        open={showDialog}
        loading={thisLoading}
        notLoading={notLoading}
        handleClose={onCloseDialog}
        userData={typedUserData}
        invoiceId={invoiceToSend}
        invoices={invoiceList}
        handleInvoices={setInvoiceList}
      />
      <ResendInvoiceManual
        open={showDialogResend}
        loading={thisLoading}
        notLoading={notLoading}
        handleClose={onCloseDialogResend}
        title="Reenviar Factura"
        invoiceToResend={invoiceToResend}
        reloadInvoice={getListInvoice}
        t={t}
      />
    </MainLayout>
  );
};
